import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { MessageSquare, CheckCircle, XCircle, AlertTriangle, Loader2, Phone, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSmsLogsPage = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLogs = async (p = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/sms-logs?page=${p}&limit=30`);
      setLogs(res.data.logs || []);
      setTotalPages(res.data.pages || 1);
      setStats(res.data.stats || {});
    } catch { toast.error('Error al cargar logs SMS'); }
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, []);

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="admin-sms-logs">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">SMS LOGS</h1>
            <p className="text-zinc-400 mt-1">Historial de SMS enviados por el portal</p>
          </div>
          <Button onClick={() => fetchLogs(page)} variant="outline" className="border-white/10 text-white">
            Actualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-surface border border-white/10 rounded-md p-4 text-center">
            <CheckCircle className="w-5 h-5 text-green-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-green-400 font-mono">{stats.total_sent || 0}</p>
            <p className="text-zinc-500 text-xs uppercase">Enviados</p>
          </div>
          <div className="bg-surface border border-white/10 rounded-md p-4 text-center">
            <XCircle className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-red-400 font-mono">{stats.total_failed || 0}</p>
            <p className="text-zinc-500 text-xs uppercase">Fallidos</p>
          </div>
          <div className="bg-surface border border-white/10 rounded-md p-4 text-center">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-yellow-400 font-mono">{stats.total_mock || 0}</p>
            <p className="text-zinc-500 text-xs uppercase">Mock (no enviados)</p>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : logs.length === 0 ? (
          <div className="bg-surface border border-white/10 rounded-md p-12 text-center">
            <MessageSquare className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No hay SMS enviados</p>
          </div>
        ) : (
          <div className="bg-surface border border-white/10 rounded-md overflow-x-auto">
            <table className="w-full" data-testid="sms-table">
              <thead>
                <tr className="border-b border-white/10 text-[10px] text-zinc-500 uppercase tracking-wider">
                  <th className="text-left px-4 py-3">FECHA</th>
                  <th className="text-left px-3 py-3">TELEFONO</th>
                  <th className="text-left px-3 py-3">USUARIO</th>
                  <th className="text-left px-3 py-3">MENSAJE</th>
                  <th className="text-center px-3 py-3">ESTADO</th>
                  <th className="text-center px-3 py-3">GATEWAY</th>
                  <th className="text-left px-3 py-3">ERROR</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{formatDate(log.created_at)}</td>
                    <td className="px-3 py-3 text-white text-sm font-mono">{log.phone}</td>
                    <td className="px-3 py-3 text-zinc-300 text-xs">{log.user_email || '-'}</td>
                    <td className="px-3 py-3 text-zinc-400 text-xs max-w-[200px] truncate">{log.message_preview}</td>
                    <td className="px-3 py-3 text-center">
                      {log.mock ? (
                        <span className="text-[10px] px-2 py-1 rounded bg-yellow-500/15 text-yellow-400">MOCK</span>
                      ) : log.success ? (
                        <span className="text-[10px] px-2 py-1 rounded bg-green-500/15 text-green-400">OK</span>
                      ) : (
                        <span className="text-[10px] px-2 py-1 rounded bg-red-500/15 text-red-400">FALLO</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center text-zinc-500 text-xs">{log.gateway || '-'}</td>
                    <td className="px-3 py-3 text-red-400/70 text-xs max-w-[150px] truncate">{log.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => { setPage(page - 1); fetchLogs(page - 1); }}>Anterior</Button>
            <span className="text-zinc-400 text-sm py-2">Pag {page}/{totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => { setPage(page + 1); fetchLogs(page + 1); }}>Siguiente</Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminSmsLogsPage;
